import { useState, useEffect } from 'react';
import Modal from '../shared/Modal';
import { mergePeople } from '../../utils/api';

// Merge a cluster of duplicate people into ONE row. The admin picks who to
// KEEP; every reference on the others (block positions, rosters, radio flags,
// roles, availability) moves onto the keeper before the duplicates are
// removed — so nothing scheduled is lost no matter which row a lead had used.
//
// Rows are addressed by their INDEX in the cluster, never by person id — the
// sheet can (temporarily) hold two rows sharing one Person ID, and id-keyed
// selection made the warning fire against the wrong row. Each non-keeper row
// also has an INCLUDE checkbox, so a cluster that caught a bystander (same
// email, different human) can merge just the true duplicates.
export default function MergePeopleModal({ open, onClose, group = [], memberships = {}, onMerged, showToast }) {
  const [keepIdx, setKeepIdx] = useState(0);
  const [included, setIncluded] = useState([]);
  const [merging, setMerging] = useState(false);

  // A REAL Brushfire registration is a NUMERIC attendee number — placeholder
  // text like "staff" in that column doesn't make a row the registered one.
  const isBrushfire = (p) => /^\d+$/.test(String(p.brushfireId || '').trim());

  useEffect(() => {
    if (!open) return;
    // Default keeper: the registered row (has a Brushfire number), else the first.
    const idx = group.findIndex(isBrushfire);
    setKeepIdx(idx === -1 ? 0 : idx);
    setIncluded(group.map(() => true));
    setMerging(false);
  }, [open, group]);

  const keeper = group[keepIdx] || null;
  const mergeTargets = group.filter((p, i) => i !== keepIdx && included[i]);

  async function handleMerge() {
    if (!keeper || mergeTargets.length === 0 || merging) return;
    setMerging(true);
    let merged = 0;
    for (const p of mergeTargets) {
      try {
        if (p.id === keeper.id) {
          // Two sheet rows sharing one Person ID can't be merged by id —
          // that's repaired automatically (same-ID repair), not merged here.
          showToast(`${p.name}: these rows share one ID — Rebecca's repair handles them, no merge needed.`);
          continue;
        }
        await mergePeople(keeper.id, p.id);
        merged++;
      } catch (err) {
        console.error('Merge failed for', p.name, err);
        showToast(`Merge failed for ${p.name}: ${err.message || 'unknown error'}`);
        break;
      }
    }
    setMerging(false);
    if (merged > 0) {
      showToast(`Merged ${merged} duplicate ${merged === 1 ? 'row' : 'rows'} — schedules and rosters moved to the kept person.`);
      if (onMerged) onMerged();
      onClose();
    }
  }

  // Keeping a non-Brushfire row while a Brushfire-registered one exists is
  // almost always wrong — the sync is tied to the registered row.
  const brushfireRows = group.filter(isBrushfire);
  const keepingWrongRow = brushfireRows.length > 0 && keeper && !isBrushfire(keeper);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Merge Duplicate People"
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={merging}>Cancel</button>
          <button
            className={`btn ${keepingWrongRow ? 'btn-danger' : 'btn-dark'}`}
            onClick={handleMerge}
            disabled={merging || !keeper || mergeTargets.length === 0}
          >
            {merging ? 'Merging…' : keepingWrongRow ? 'Merge anyway (not recommended)' : `Merge ${mergeTargets.length} into ${keeper?.name || 'selected'}`}
          </button>
        </>
      }
    >
      <div className="notice-bar" style={{ marginBottom: 16 }}>
        Pick which row to <strong>keep</strong> (radio), and uncheck anyone who is actually a
        <strong> different person</strong> — unchecked rows are left alone. Everything on the merged
        rows — scheduled positions, team rosters, radio flags, availability — moves onto the kept
        person first, then the duplicates are removed. Nothing scheduled is lost.
      </div>

      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
        {group.map((p, i) => {
          const teams = (memberships[p.id] || []).map(t => t.teamName).join(', ');
          const isKeep = i === keepIdx;
          return (
            <div
              key={i}
              style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px', borderBottom: i < group.length - 1 ? '1px solid var(--border)' : 'none', background: isKeep ? 'var(--off)' : (included[i] ? 'var(--white)' : '#fafafa'), opacity: isKeep || included[i] ? 1 : 0.55 }}
            >
              <input
                type="radio"
                name="merge-keeper"
                checked={isKeep}
                onChange={() => setKeepIdx(i)}
                title="Keep this row"
                style={{ accentColor: 'var(--black)', marginTop: 3, flexShrink: 0, cursor: 'pointer' }}
              />
              {!isKeep && (
                <input
                  type="checkbox"
                  checked={!!included[i]}
                  onChange={() => setIncluded(prev => prev.map((v, vi) => vi === i ? !v : v))}
                  title={included[i] ? 'Will be merged into the kept row — uncheck if this is a different person' : 'Excluded — this row will be left alone'}
                  style={{ accentColor: 'var(--black)', marginTop: 3, flexShrink: 0, cursor: 'pointer' }}
                />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'var(--font-h)', fontSize: 14, fontWeight: 800, color: 'var(--black)' }}>
                  {p.name}
                  {isBrushfire(p) && (
                    <span className="pill" style={{ fontSize: 9, padding: '2px 8px', marginLeft: 8, background: '#FFDB7C', color: '#5a4200', border: '1px solid #e0b84a', letterSpacing: '0.04em' }}>
                      ★ BRUSHFIRE — KEEP THIS ONE
                    </span>
                  )}
                  {isKeep && <span className="pill pill-green" style={{ fontSize: 9, padding: '2px 8px', marginLeft: 8 }}>KEEP</span>}
                  {!isKeep && !included[i] && <span className="pill pill-gray" style={{ fontSize: 9, padding: '2px 8px', marginLeft: 8 }}>LEFT ALONE</span>}
                </div>
                <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 2 }}>
                  {p.email || 'no email'} · {isBrushfire(p) ? `Registered (#${p.brushfireId})` : 'Not registered'}
                  {p.passLevel ? ` · ${p.passLevel}` : ''}
                </div>
                <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--mid)', marginTop: 2 }}>
                  {teams ? `Teams: ${teams}` : 'No teams'}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {keepingWrongRow && (
        <div style={{ marginTop: 12, background: '#fff0f0', border: '1px solid #c00', borderRadius: 'var(--radius)', padding: '11px 14px', fontFamily: 'var(--font-b)', fontSize: 13, color: '#900' }}>
          ⚠ You've selected a row that is <strong>NOT</strong> the Brushfire registration. The Brushfire sync is tied
          to the ★ row — keeping a different one means the sync will re-create the duplicate on its next run.
          Keep the ★ row unless you have a specific reason not to.
        </div>
      )}
      {brushfireRows.length > 1 && (
        <div style={{ marginTop: 12, background: '#fffbf0', border: '1px solid #d4920a', borderRadius: 'var(--radius)', padding: '11px 14px', fontFamily: 'var(--font-b)', fontSize: 13, color: '#7a4f0a' }}>
          This cluster has <strong>{brushfireRows.length} Brushfire registrations</strong> — the same person registered
          more than once. Keep either ★ row: the merged-away registration is remembered, so the sync will
          <strong> not</strong> bring it back. Cancelling the extra ticket in Brushfire is still worth doing for refunds.
        </div>
      )}
      <div style={{ marginTop: 12, fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)' }}>
        Blank fields on the kept row are filled in from the duplicates automatically.
      </div>
    </Modal>
  );
}
